import type { AddFavoriteRequest, FavoriteListResponse } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * G-01 — the person's own favourites, read once per session and replaced
 * whole on every write: each mutation answers with the full list, so the bar
 * and the star never merge and can never disagree.
 */
export const FAVORITES_KEY = ['favorites'] as const;

export function useFavorites() {
  return useQuery({
    queryKey: FAVORITES_KEY,
    queryFn: async () => {
      const res = await api.favorites.list();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    staleTime: 5 * 60_000,
    // The bar is not worth a retry storm; a missing bar is invisible, and the
    // star simply waits for the list before it can be pressed.
    retry: 1,
  });
}

function useReplaceList() {
  const queryClient = useQueryClient();
  return (list: FavoriteListResponse) => queryClient.setQueryData(FAVORITES_KEY, list);
}

export function useAddFavorite() {
  const replace = useReplaceList();
  return useMutation({
    mutationFn: async (input: AddFavoriteRequest) => {
      const res = await api.favorites.add({ body: input });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: replace,
  });
}

export function useRemoveFavorite() {
  const replace = useReplaceList();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.favorites.remove({ params: { id }, body: {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: replace,
  });
}

export function useReorderFavorites() {
  const replace = useReplaceList();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.favorites.reorder({ body: { ids } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: replace,
  });
}
